import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import afdelingen from '../data/afdelingen.json';
import { weekLabel, volledigeDatum } from '../utils/datum';

const PRIO_CL = { spoed: '#A32D2D', hoog: '#854F0B', normaal: '#3B6D11' };
const PRIO_BG = { spoed: '#FDEDEC', hoog: '#FDF3E3', normaal: '#EAF3DE' };
const MAT_CL  = { te_bestellen: '#C0392B', besteld: '#B5660E', geleverd: '#185FA5', in_stock: '#1A7A3C' };
const MAT_NL  = { te_bestellen: 'Mat. te bestellen', besteld: 'Mat. besteld', geleverd: 'Mat. geleverd', in_stock: 'In stock' };

const REFRESH_SEC = 60;

const KOLOMMEN = [
  { key: 'operatie',   label: 'Operatie'      },
  { key: 'order',      label: 'Order'         },
  { key: 'klant',      label: 'Klant / Artikel' },
  { key: 'aantal',     label: 'Aant.'         },
  { key: 'leverdatum', label: 'Leverdatum'    },
  { key: 'dagen',      label: 'Dagen'         },
  { key: 'voortgang',  label: 'Voortgang'     },
];

function tegelKleur(aantal) {
  if (aantal === 0) return { bg: '#F4F4F4', tekst: '#AAAAAA', rand: '#DDDDDD' };
  if (aantal <= 2)  return { bg: '#E9F7EF', tekst: '#1A7A3C', rand: '#82C96B' };
  if (aantal <= 5)  return { bg: '#FDF3E3', tekst: '#B5660E', rand: '#F0A500' };
  return              { bg: '#FDEDEC', tekst: '#B03020', rand: '#E05040' };
}
function tegelActief(aantal) {
  if (aantal === 0) return { bg: '#888', rand: '#666' };
  if (aantal <= 2)  return { bg: '#1A7A3C', rand: '#137030' };
  if (aantal <= 5)  return { bg: '#B5660E', rand: '#9A5509' };
  return              { bg: '#B03020', rand: '#8C2418' };
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export default function WerkvloerPage({ user, onUserUpdate }) {
  const isMobile = useIsMobile();

  // Bepaal standaard afdeling op basis van gebruikersprofiel
  const standaardAfd = afdelingen.find(a => a.id === user?.standaard_afdeling) || null;

  const [stappen, setStappen]         = useState([]);
  const [laden, setLaden]             = useState(true);
  const [filterAfd, setFilterAfd]     = useState(standaardAfd);
  const [zoek, setZoek]               = useState('');
  const [sortKolom, setSortKolom]     = useState('dagen');
  const [sortAsc, setSortAsc]         = useState(true);
  const [opmerkingen, setOpmerkingen] = useState({});
  const [gelezen, setGelezen]         = useState({});
  const [feedbackPopup, setFeedbackPopup] = useState(null);
  const [feedback, setFeedback]       = useState({});
  const [bezig, setBezig]             = useState(null);
  const [materiaalOpen, setMateriaalOpen]   = useState({});
  const [materiaalVorm, setMateriaalVorm]   = useState({});
  const [materiaalBezig, setMateriaalBezig] = useState({});
  const [countdown, setCountdown]     = useState(REFRESH_SEC);
  const countdownRef                  = useRef(REFRESH_SEC);
  const nav = useNavigate();

  const laad = useCallback(async () => {
    try {
      const r = await api.get('/orders/actieve-stappen');
      setStappen(r.data);
    } catch { /* stil falen */ }
    setLaden(false);
    countdownRef.current = REFRESH_SEC;
    setCountdown(REFRESH_SEC);
  }, []);

  useEffect(() => {
    laad();
    const interval = setInterval(() => {
      countdownRef.current -= 1;
      setCountdown(countdownRef.current);
      if (countdownRef.current <= 0) laad();
    }, 1000);
    return () => clearInterval(interval);
  }, [laad]);

  const klikKolom = (key) => {
    if (sortKolom === key) setSortAsc(a => !a);
    else { setSortKolom(key); setSortAsc(true); }
  };

  const SortPijl = ({ kolom }) => {
    if (sortKolom !== kolom) return <span style={{ opacity: 0.25, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3 }}>{sortAsc ? '↑' : '↓'}</span>;
  };

  const stappenVoorAfd = (afd) =>
    afd.operatieNummers.length === 0
      ? []
      : stappen.filter(s => afd.operatieNummers.includes(parseInt(s.operatie_nummer)));

  const gefilterd = (() => {
    let lijst = filterAfd ? stappenVoorAfd(filterAfd) : stappen;
    if (zoek) {
      const q = zoek.toLowerCase();
      lijst = lijst.filter(s =>
        `${s.ordernummer} ${s.klant} ${s.artikel_omschrijving} ${s.label || s.operatie_naam}`.toLowerCase().includes(q)
      );
    }
    lijst = [...lijst].sort((a, b) => {
      let va, vb;
      switch (sortKolom) {
        case 'operatie':   va = a.label || a.operatie_naam; vb = b.label || b.operatie_naam; break;
        case 'order':      va = a.ordernummer; vb = b.ordernummer; break;
        case 'klant':      va = a.klant; vb = b.klant; break;
        case 'aantal':     va = parseInt(a.aantal) || 0; vb = parseInt(b.aantal) || 0; break;
        case 'leverdatum': va = a.leverdatum; vb = b.leverdatum; break;
        case 'voortgang':
          va = a.totaal_stappen > 0 ? a.voltooide_stappen / a.totaal_stappen : 0;
          vb = b.totaal_stappen > 0 ? b.voltooide_stappen / b.totaal_stappen : 0; break;
        case 'dagen':
        default:
          va = parseInt(a.dagen_resterend); vb = parseInt(b.dagen_resterend); break;
      }
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return lijst;
  })();

  const slaAfdelingOp = async (afdId) => {
    try {
      const res = await api.patch('/auth/afdeling', { standaard_afdeling: afdId || null });
      if (onUserUpdate) onUserUpdate(res.data);
    } catch { /* stil */ }
  };

  const openFeedback = (stap) => {
    setFeedbackPopup(stap);
    setFeedback(f => ({ ...f, [stap.id]: f[stap.id] || { tijd: '', materiaal: '', onduidelijk: '', overige: '' } }));
  };

  const vinkAf = async (stap) => {
    const fb = feedback[stap.id] || {};
    const heeftFeedback = fb.tijd || fb.materiaal || fb.onduidelijk || fb.overige;
    setFeedbackPopup(null);
    setBezig(stap.id);
    try {
      await api.patch(`/orders/${stap.order_id}/stap/${stap.id}/voltooid`, {
        opmerking: opmerkingen[stap.id] || '',
        feedback: heeftFeedback ? fb : null,
      });
      setStappen(a => a.filter(s => s.id !== stap.id));
      setOpmerkingen(p => { const n = { ...p }; delete n[stap.id]; return n; });
      setGelezen(p => { const n = { ...p }; delete n[stap.id]; return n; });
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBezig(null);
  };

  const openMateriaalForm = (stap) => {
    setMateriaalVorm(v => ({
      ...v,
      [stap.id]: v[stap.id] !== undefined ? v[stap.id] : {
        naam: stap.materiaal_naam || '',
        referentie: stap.materiaal_referentie || '',
        hoeveelheid: stap.materiaal_hoeveelheid || '',
        eenheid: stap.materiaal_eenheid || 'stuks',
      }
    }));
    setMateriaalOpen(o => ({ ...o, [stap.id]: !o[stap.id] }));
  };

  const slaMateriaalOp = async (stap) => {
    const vorm = materiaalVorm[stap.id] || {};
    if (!vorm.naam?.trim()) { alert('Vul een materiaalnaam in'); return; }
    setMateriaalBezig(b => ({ ...b, [stap.id]: true }));
    try {
      await api.patch(`/orders/${stap.order_id}/stap/${stap.id}/materiaal`, {
        naam: vorm.naam.trim(),
        referentie: vorm.referentie?.trim() || null,
        hoeveelheid: vorm.hoeveelheid ? parseFloat(vorm.hoeveelheid) : null,
        eenheid: vorm.eenheid || 'stuks',
      });
      setStappen(s => s.map(x => x.id === stap.id ? {
        ...x,
        materiaal_naam: vorm.naam.trim(),
        materiaal_referentie: vorm.referentie?.trim() || null,
        materiaal_hoeveelheid: vorm.hoeveelheid ? parseFloat(vorm.hoeveelheid) : null,
        materiaal_eenheid: vorm.eenheid || 'stuks',
      } : x));
      setMateriaalOpen(o => ({ ...o, [stap.id]: false }));
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setMateriaalBezig(b => ({ ...b, [stap.id]: false }));
  };

  const MateriaalFormBlock = ({ stap }) => {
    const vorm = materiaalVorm[stap.id] || {};
    const setV = (k, v) => setMateriaalVorm(f => ({ ...f, [stap.id]: { ...f[stap.id], [k]: v } }));
    const mb = !!materiaalBezig[stap.id];
    const EENHEDEN = ['stuks', 'meter', 'kg', 'liter', 'cm', 'mm'];
    return (
      <div style={{ background: '#F0FBF6', border: '1px solid #82C96B', borderRadius: 8, padding: 12, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#267A5C', marginBottom: 8 }}>📦 Welk materiaal gebruik je?</div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 11, color: '#267A5C', display: 'block', marginBottom: 2 }}>Materiaal * (bv. S355 staalstaf ⌀40mm)</label>
          <input value={vorm.naam || ''} onChange={e => setV('naam', e.target.value)}
            placeholder="Materiaalnaam / omschrijving"
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #82C96B', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 2 }}>Referentie / art.nr. (optioneel)</label>
            <input value={vorm.referentie || ''} onChange={e => setV('referentie', e.target.value)}
              placeholder="bv. KW-40-S355"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #DDD', borderRadius: 5, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 2 }}>Hoeveelheid (optioneel)</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input type="number" min="0" step="0.01" value={vorm.hoeveelheid || ''} onChange={e => setV('hoeveelheid', e.target.value)}
                placeholder="bv. 2.5"
                style={{ flex: 1, padding: '6px 6px', border: '1px solid #DDD', borderRadius: 5, fontSize: 12, minWidth: 0 }} />
              <select value={vorm.eenheid || 'stuks'} onChange={e => setV('eenheid', e.target.value)}
                style={{ padding: '6px 4px', border: '1px solid #DDD', borderRadius: 5, fontSize: 12, width: 66 }}>
                {EENHEDEN.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => slaMateriaalOp(stap)} disabled={mb || !vorm.naam?.trim()}
            style={{ flex: 1, padding: '8px', background: mb || !vorm.naam?.trim() ? '#AAA' : '#267A5C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: mb || !vorm.naam?.trim() ? 'default' : 'pointer' }}>
            {mb ? 'Opslaan...' : 'Opslaan'}
          </button>
          <button onClick={() => setMateriaalOpen(o => ({ ...o, [stap.id]: false }))}
            style={{ padding: '8px 14px', background: '#fff', border: '1px solid #CCC', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#555' }}>
            ✕
          </button>
        </div>
      </div>
    );
  };

  const totaal = stappen.length;
  const allesActief = filterAfd === null;
  const allesKl = tegelActief(totaal);

  const FeedbackModal = () => {
    const stap = feedbackPopup;
    if (!stap) return null;
    const fb = feedback[stap.id] || {};
    const setFb = (key, val) => setFeedback(f => ({ ...f, [stap.id]: { ...f[stap.id], [key]: val } }));
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1F3864', marginBottom: 4 }}>Stap afronden</div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>{stap.label || stap.operatie_naam} — {stap.ordernummer}</div>

          {[
            ['Opmerking (optioneel)', 'text', 'opmerking', 'bv. klaar voor volgende stap', false],
            ['Bewerkingstijd (min)', 'number', 'tijd', 'bv. 45', false],
            ['Extra verbruikt materiaal', 'text', 'materiaal', 'bv. 2 slijpschijven extra', false],
          ].map(([label, type, key, placeholder]) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
              <input type={type} placeholder={placeholder}
                value={key === 'opmerking' ? (opmerkingen[stap.id] || '') : (fb[key] || '')}
                onChange={e => key === 'opmerking'
                  ? setOpmerkingen(p => ({ ...p, [stap.id]: e.target.value }))
                  : setFb(key, e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          ))}

          {[['Onduidelijkheden / problemen', 'onduidelijk', 'Wat was onduidelijk of liep fout?'],
            ['Overige opmerkingen', 'overige', 'Optioneel...']].map(([label, key, ph]) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
              <textarea rows={2} placeholder={ph} value={fb[key] || ''} onChange={e => setFb(key, e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={() => setFeedbackPopup(null)}
              style={{ flex: 1, padding: '10px', border: '1px solid #DDD', borderRadius: 8, fontSize: 13, cursor: 'pointer', background: '#fff', color: '#555' }}>
              Annuleren
            </button>
            <button onClick={() => vinkAf(stap)}
              style={{ flex: 2, padding: '10px', background: '#1A7A3C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Stap voltooien ✓
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── MOBIELE KAART ──────────────────────────────────────────────────────────
  const MobieleKaart = ({ stap }) => {
    const telaat = stap.dagen_resterend < 0;
    const bijna  = !telaat && stap.dagen_resterend <= 3;
    const voortgang = stap.totaal_stappen > 0
      ? Math.round((parseInt(stap.voltooide_stappen) / parseInt(stap.totaal_stappen)) * 100) : 0;
    const isBezig = bezig === stap.id;

    return (
      <div style={{
        background: '#fff', borderRadius: 10, padding: 14, marginBottom: 10,
        border: `1.5px solid ${telaat ? '#F5C0C0' : '#E8E8E8'}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        {/* Bovenste rij */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1F3864' }}>{stap.label || stap.operatie_naam}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span onClick={() => nav(`/orders/${stap.order_id}`, { state: { from: '/productie' } })}
                style={{ fontWeight: 700, fontSize: 13, color: '#185FA5', cursor: 'pointer' }}>
                {stap.ordernummer}
              </span>
              <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, background: PRIO_BG[stap.prioriteit], color: PRIO_CL[stap.prioriteit] }}>
                {stap.prioriteit}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
            <div style={{ fontSize: 12, color: '#555' }} title={volledigeDatum(stap.leverdatum)}>{weekLabel(stap.leverdatum)}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: telaat ? '#C0392B' : bijna ? '#B5660E' : '#3B6D11' }}>
              {telaat ? `${Math.abs(stap.dagen_resterend)}d te laat` : `${stap.dagen_resterend}d`}
            </div>
          </div>
        </div>

        {/* Klant / artikel */}
        <div style={{ fontSize: 13, color: '#333', marginBottom: 6 }}>
          <span style={{ fontWeight: 500 }}>{stap.klant}</span>
          {stap.artikel_omschrijving && <span style={{ color: '#888', marginLeft: 6, fontSize: 12 }}>{stap.artikel_omschrijving}</span>}
        </div>

        {/* Materiaal & extern */}
        {stap.materiaal_status && stap.materiaal_status !== 'in_stock' && (
          <div style={{ fontSize: 11, color: MAT_CL[stap.materiaal_status], marginBottom: 6 }}>{MAT_NL[stap.materiaal_status]}</div>
        )}
        {stap.is_extern && <div style={{ fontSize: 11, color: '#633806', marginBottom: 6 }}>Extern: {stap.externe_partij}</div>}

        {/* Voortgang */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1, height: 5, background: '#EEE', borderRadius: 99 }}>
            <div style={{ width: `${voortgang}%`, height: 5, background: voortgang === 100 ? '#1A7A3C' : '#185FA5', borderRadius: 99 }} />
          </div>
          <span style={{ fontSize: 11, color: '#888', minWidth: 30 }}>{voortgang}%</span>
          <span style={{ fontSize: 10, color: '#AAA' }}>stap {parseInt(stap.voltooide_stappen)+1}/{stap.totaal_stappen}</span>
        </div>

        {/* Instructies */}
        {stap.instructies && (
          <div style={{ background: '#FFFBEB', border: '1px solid #F0D060', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7F6000', marginBottom: 2 }}>📋 INSTRUCTIES:</div>
            <div style={{ fontSize: 12, color: '#4A3000' }}>{stap.instructies}</div>
            {!gelezen[stap.id] && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7F6000', cursor: 'pointer', marginTop: 6 }}>
                <input type="checkbox" onChange={() => setGelezen(g => ({ ...g, [stap.id]: true }))} />
                Ik heb de instructies gelezen
              </label>
            )}
          </div>
        )}

        {/* Materiaalgebruik */}
        {stap.materiaal_naam && !materiaalOpen[stap.id] && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#267A5C', background: '#EBF8F2', border: '1px solid #82C96B', borderRadius: 5, padding: '3px 9px' }}>
              📦 {stap.materiaal_naam}{stap.materiaal_hoeveelheid ? ` · ${parseFloat(stap.materiaal_hoeveelheid)} ${stap.materiaal_eenheid || 'stuks'}` : ''}
            </span>
            <button onClick={() => openMateriaalForm(stap)} style={{ fontSize: 11, color: '#267A5C', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>wijzigen</button>
          </div>
        )}
        {materiaalOpen[stap.id] && <MateriaalFormBlock stap={stap} />}
        {!stap.materiaal_naam && !materiaalOpen[stap.id] && (
          <button onClick={() => openMateriaalForm(stap)} style={{
            display: 'flex', alignItems: 'center', gap: 5, width: '100%', marginBottom: 8,
            padding: '7px 12px', background: '#F4FBF7', border: '1px dashed #82C96B',
            borderRadius: 7, cursor: 'pointer', fontSize: 12, color: '#267A5C',
          }}>
            📦 Materiaal invoeren (optioneel)
          </button>
        )}

        {/* Knop */}
        <button onClick={() => openFeedback(stap)}
          disabled={isBezig || (stap.instructies && !gelezen[stap.id])}
          style={{
            width: '100%', padding: '11px', borderRadius: 8, border: 'none',
            background: isBezig ? '#AAA' : (stap.instructies && !gelezen[stap.id]) ? '#CCC' : '#1A7A3C',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: isBezig || (stap.instructies && !gelezen[stap.id]) ? 'not-allowed' : 'pointer',
          }}>
          {isBezig ? 'Bezig...' : 'Stap voltooien ✓'}
        </button>
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '12px 10px' : 16, maxWidth: 960, margin: '0 auto' }}>
      <FeedbackModal />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 17 : 20, fontWeight: 600, color: '#1F3864' }}>Productie</h1>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Tik op een afdeling om taken te zien</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {['planner','admin'].includes(user?.rol) && (
            <button onClick={() => nav('/print-planning')} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: '#fff', border: '1px solid #DDD', borderRadius: 8,
              padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: '#555',
            }}>
              🖨️ {isMobile ? '' : 'Print'}
            </button>
          )}
        <button onClick={laad} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: '1px solid #DDD', borderRadius: 8,
          padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: '#555',
        }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: countdown <= 10 ? '#C0392B' : '#1A7A3C' }} />
          {countdown}s
        </button>
        </div>
      </div>

      {/* Afdeling-tegels */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(110px, 1fr))', gap: 7, marginBottom: 16 }}>
        <button onClick={() => { setFilterAfd(null); slaAfdelingOp(null); }} style={{
          background: allesActief ? allesKl.bg : '#F4F4F4',
          border: `2.5px solid ${allesActief ? allesKl.rand : '#DDD'}`,
          borderRadius: 10, padding: '10px 8px', cursor: 'pointer',
          textAlign: 'left', WebkitTapHighlightColor: 'transparent',
          minHeight: 70, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: allesActief ? '#fff' : '#AAA' }}>
            {laden ? '–' : totaal}
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, color: allesActief ? '#fff' : '#666', marginTop: 4, lineHeight: 1.2 }}>
            Alle afdelingen
          </div>
        </button>

        {afdelingen.map(afd => {
          const n = laden ? null : stappenVoorAfd(afd).length;
          const actief = filterAfd?.id === afd.id;
          const kl = tegelKleur(n ?? 0);
          const aKl = tegelActief(n ?? 0);
          const isStandaard = user?.standaard_afdeling === afd.id;
          return (
            <button key={afd.id} onClick={() => { setFilterAfd(actief ? null : afd); if (!actief) slaAfdelingOp(afd.id); else slaAfdelingOp(null); }} style={{
              background: actief ? aKl.bg : kl.bg,
              border: `2.5px solid ${actief ? aKl.rand : isStandaard ? '#185FA5' : kl.rand}`,
              borderRadius: 10, padding: '10px 8px', cursor: 'pointer',
              textAlign: 'left', WebkitTapHighlightColor: 'transparent',
              minHeight: 70, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              position: 'relative',
            }}>
              {isStandaard && (
                <div style={{ position: 'absolute', top: 4, right: 5, fontSize: 9, color: '#185FA5', fontWeight: 700 }}>★</div>
              )}
              <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: actief ? '#fff' : kl.tekst }}>
                {laden ? '–' : n}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: actief ? '#fff' : kl.tekst, marginTop: 4, lineHeight: 1.2 }}>
                {afd.naam}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tip standaard afdeling */}
      {!user?.standaard_afdeling && !laden && (
        <div style={{ background: '#E6F1FB', border: '1px solid #B8D4F0', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#185FA5', marginBottom: 12 }}>
          💡 Klik op een afdeling om die als jouw standaard in te stellen — dan zie je die automatisch bij het opstarten.
        </div>
      )}

      {/* Zoek + sorteer */}
      <div style={{ marginBottom: 12 }}>
        <input value={zoek} onChange={e => setZoek(e.target.value)}
          placeholder="Zoeken op order, klant, operatie..."
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #DDD', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }} />
        {/* Sorteer knoppen — altijd zichtbaar (op desktop ook) */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#888', alignSelf: 'center' }}>Sortering:</span>
          {[
            ['dagen',      'Urgentie'   ],
            ['operatie',   'Operatie'   ],
            ['order',      'Order nr.'  ],
            ['klant',      'Klant'      ],
            ['leverdatum', 'Leverdatum' ],
            ['voortgang',  'Voortgang'  ],
          ].map(([k, l]) => (
            <button key={k} onClick={() => klikKolom(k)} style={{
              padding: '5px 11px', border: sortKolom === k ? '1.5px solid #1F3864' : '1px solid #DDD',
              borderRadius: 99, fontSize: 12, cursor: 'pointer',
              background: sortKolom === k ? '#1F3864' : '#fff',
              color: sortKolom === k ? '#fff' : '#555',
              fontWeight: sortKolom === k ? 600 : 400,
            }}>
              {l}{sortKolom === k ? (sortAsc ? ' ↑' : ' ↓') : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Sectieheader */}
      {filterAfd && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1F3864' }}>{filterAfd.naam}</div>
          <div style={{ fontSize: 12, color: '#888' }}>— {gefilterd.length} taak{gefilterd.length !== 1 ? 'en' : ''}</div>
          <button onClick={() => { setFilterAfd(null); slaAfdelingOp(null); }}
            style={{ marginLeft: 'auto', fontSize: 12, color: '#185FA5', background: 'none', border: 'none', cursor: 'pointer' }}>
            Toon alles
          </button>
        </div>
      )}

      {/* Inhoud */}
      {laden ? (
        <div style={{ color: '#888', fontSize: 13, padding: 24, textAlign: 'center' }}>Laden...</div>
      ) : gefilterd.length === 0 ? (
        <div style={{ background: '#EAF3DE', border: '1px solid #C0DD97', borderRadius: 10, padding: '18px 20px', color: '#27500A', fontSize: 14 }}>
          Geen actieve taken{filterAfd ? ` voor "${filterAfd.naam}"` : ''}{zoek ? ` met "${zoek}"` : ''}.
        </div>
      ) : isMobile ? (
        // ── MOBIEL: kaarten ────────────────────────────────────────────────
        <div>
          {gefilterd.map(stap => <MobieleKaart key={stap.id} stap={stap} />)}
        </div>
      ) : (
        // ── DESKTOP: tabel ─────────────────────────────────────────────────
        <div style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1F3864', color: '#fff' }}>
                {KOLOMMEN.map(k => (
                  <th key={k.key} onClick={() => klikKolom(k.key)} style={{
                    padding: '10px 12px', textAlign: 'left', fontWeight: 500, fontSize: 12,
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                  }}>
                    {k.label}<SortPijl kolom={k.key} />
                  </th>
                ))}
                <th style={{ padding: '10px 12px', fontWeight: 500, fontSize: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {gefilterd.map((stap, i) => {
                const telaat = stap.dagen_resterend < 0;
                const bijna  = !telaat && stap.dagen_resterend <= 3;
                const voortgang = stap.totaal_stappen > 0
                  ? Math.round((parseInt(stap.voltooide_stappen) / parseInt(stap.totaal_stappen)) * 100) : 0;
                const isBezig = bezig === stap.id;
                const rij_bg = telaat ? '#FFF5F5' : i % 2 === 0 ? '#fff' : '#FAFAFA';
                const td = { padding: '9px 12px', borderBottom: stap.instructies ? 'none' : '1px solid #F0F0F0', verticalAlign: 'middle' };

                return (
                  <React.Fragment key={stap.id}>
                    <tr style={{ background: rij_bg }}
                      onMouseEnter={e => e.currentTarget.style.background = '#EEF4FB'}
                      onMouseLeave={e => e.currentTarget.style.background = rij_bg}>

                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {stap.familie_kleur && <div style={{ width: 4, height: 20, borderRadius: 2, background: stap.familie_kleur, flexShrink: 0 }} />}
                          <div>
                            <div style={{ fontWeight: 600, color: '#1F3864' }}>{stap.label || stap.operatie_naam}</div>
                            {stap.is_extern && <div style={{ fontSize: 11, color: '#633806' }}>extern: {stap.externe_partij}</div>}
                          </div>
                        </div>
                      </td>

                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span onClick={() => nav(`/orders/${stap.order_id}`, { state: { from: '/productie' } })}
                            style={{ fontWeight: 700, color: '#185FA5', cursor: 'pointer', fontSize: 12 }}>
                            {stap.ordernummer}
                          </span>
                          <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 99, background: PRIO_BG[stap.prioriteit], color: PRIO_CL[stap.prioriteit] }}>
                            {stap.prioriteit}
                          </span>
                        </div>
                        {stap.materiaal_status && stap.materiaal_status !== 'in_stock' && (
                          <div style={{ fontSize: 11, color: MAT_CL[stap.materiaal_status], marginTop: 2 }}>{MAT_NL[stap.materiaal_status]}</div>
                        )}
                      </td>

                      <td style={td}>
                        <div style={{ fontWeight: 500, color: '#333' }}>{stap.klant}</div>
                        <div style={{ fontSize: 11, color: '#888', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stap.artikel_omschrijving}</div>
                      </td>

                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#555' }}>{stap.aantal}</td>

                      <td style={td}>
                        <div style={{ fontSize: 12 }} title={volledigeDatum(stap.leverdatum)}>{weekLabel(stap.leverdatum)}</div>
                      </td>

                      <td style={td}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: telaat ? '#C0392B' : bijna ? '#B5660E' : '#3B6D11' }}>
                          {telaat ? `${Math.abs(stap.dagen_resterend)}d te laat` : `${stap.dagen_resterend}d`}
                        </div>
                      </td>

                      <td style={{ ...td, minWidth: 90 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, height: 5, background: '#EEE', borderRadius: 99 }}>
                            <div style={{ width: `${voortgang}%`, height: 5, background: voortgang === 100 ? '#1A7A3C' : '#185FA5', borderRadius: 99 }} />
                          </div>
                          <span style={{ fontSize: 11, color: '#888', minWidth: 28 }}>{voortgang}%</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#AAA', marginTop: 2 }}>stap {parseInt(stap.voltooide_stappen)+1}/{stap.totaal_stappen}</div>
                      </td>

                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button onClick={() => openMateriaalForm(stap)}
                            title={stap.materiaal_naam ? `📦 ${stap.materiaal_naam}` : 'Materiaal invoeren'}
                            style={{
                              padding: '6px 10px',
                              background: stap.materiaal_naam ? '#EBF8F2' : '#F4F4F4',
                              color: stap.materiaal_naam ? '#267A5C' : '#888',
                              border: stap.materiaal_naam ? '1px solid #82C96B' : '1px solid #DDD',
                              borderRadius: 6, fontSize: 12, cursor: 'pointer',
                            }}>
                            📦
                          </button>
                          <button onClick={() => openFeedback(stap)}
                            disabled={isBezig || (stap.instructies && !gelezen[stap.id])}
                            title={stap.instructies && !gelezen[stap.id] ? 'Bevestig eerst dat je de instructies hebt gelezen' : ''}
                            style={{
                              padding: '6px 14px',
                              background: isBezig ? '#AAA' : (stap.instructies && !gelezen[stap.id]) ? '#CCC' : '#1A7A3C',
                              color: '#fff', border: 'none', borderRadius: 6,
                              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                              cursor: isBezig || (stap.instructies && !gelezen[stap.id]) ? 'not-allowed' : 'pointer',
                            }}>
                            {isBezig ? 'Bezig...' : 'Voltooid ✓'}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {stap.instructies && (
                      <tr style={{ background: '#FFFBEB', borderBottom: materiaalOpen[stap.id] ? 'none' : '1px solid #F0F0F0' }}>
                        <td colSpan={8} style={{ padding: '8px 14px 10px 24px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#7F6000' }}>📋 INSTRUCTIES: </span>
                              <span style={{ fontSize: 12, color: '#4A3000' }}>{stap.instructies}</span>
                            </div>
                            {!gelezen[stap.id] && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7F6000', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                <input type="checkbox" onChange={() => setGelezen(g => ({ ...g, [stap.id]: true }))} />
                                Gelezen
                              </label>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {materiaalOpen[stap.id] && (
                      <tr style={{ background: '#F0FBF6', borderBottom: '1px solid #C0E8D0' }}>
                        <td colSpan={8} style={{ padding: '10px 14px 12px 24px' }}>
                          <MateriaalFormBlock stap={stap} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!laden && gefilterd.length > 0 && (
        <div style={{ textAlign: 'center', fontSize: 12, color: '#AAA', marginTop: 8 }}>
          {gefilterd.length} taak{gefilterd.length !== 1 ? 'en' : ''} weergegeven
        </div>
      )}
    </div>
  );
}
